import { ReactNode, useContext, useEffect, useMemo, useState } from 'react';
import { LiveBettingContext } from 'context/liveBettingContext';
import { LiveEvent } from 'types/LiveEvent';
import { useTranslation } from 'react-i18next';
import type { TFunction } from 'i18next';
import classNames from 'classnames';
import { Loader } from 'components/Loader/Loader';
import {
  flagUrl,
  getMatchDetails,
  getMatchStream,
  getMatchTracker,
  MatchStream,
  MatchTracker,
  HistoryMatch,
  LeagueTable,
  MatchDetails,
  TeamLineup,
  TeamRef,
  teamLogoUrl,
} from 'services/api/matchDetailsService';
import { formatDateTime } from 'helpers/dateHelper';
import { LivePitch } from 'components/LivePitch/LivePitch';
import { MatchVideo } from 'components/MatchVideo/MatchVideo';
import { BetZoneTracker } from 'components/BetZoneTracker/BetZoneTracker';
import { LIVE_TRACKER } from 'config/constants';
import styles from './MatchInsights.module.css';

export type Tab =
  | 'pitch'
  | 'video'
  | 'overview'
  | 'timeline'
  | 'form'
  | 'table'
  | 'lineups';

/** A live match without a BetZone tracker is asked again this often (it may
 *  be paired on the server a little later). */
const TRACKER_RETRY_MS = 30_000;

/** A live match's details are fetched again this often. */
const LIVE_REFRESH_MS = 45_000;

/** Stats in the order they are worth reading; the rest follow. */
const STAT_ORDER = [
  'goals',
  'possession_rt',
  'goalattempts',
  'on_target',
  'off_target',
  'shots_blocked',
  'attacks',
  'dangerous_attacks',
  'corners',
  'key_passes',
  'passing_accuracy',
  'crosses',
  'fouls',
  'freekicks',
  'offsides',
  'yellowcards',
  'redcards',
  'penalties',
  'saves',
  'goalkicks',
  'throwins',
  'substitutions',
];

/** Stats that are shares (0-1); shown as percentages. */
const RATIO_STATS = new Set([
  'passing_accuracy',
  'crossing_accuracy',
  'action_areas',
]);

/** Timeline lines that are the story of a match (the rest is detail). */
const KEY_EVENT =
  /goal|card|penalt|substitut|var\b|own goal|score after|half score|disallowed/i;
const NOISE_EVENT = /assist|shot on target|shot off target|race to/i;

interface Props {
  fi: number | string;
  /** Opens on this tab. */
  initialTab?: Tab;
  /** Leave out the teams and score on top (the page already shows them). */
  hideScoreboard?: boolean;
  /** The past meetings of the two teams: in the Form tab (include), left
   *  out (exclude, the page shows them in a tab of their own) or alone
   *  (only, without the tabs). */
  h2h?: 'include' | 'exclude' | 'only';
  /** Our odds of the match in play, under the pitch (Live) and the video,
   *  so a player can bet while watching. */
  liveOdds?: ReactNode;
  /** In the top card, under the teams. */
  heroOdds?: ReactNode;
}

export const MatchInsights = ({
  fi,
  initialTab = 'overview',
  hideScoreboard = false,
  h2h = 'include',
  liveOdds,
  heroOdds,
}: Props) => {
  const { t } = useTranslation();
  const [details, setDetails] = useState<MatchDetails | null>(null);
  const [loading, setLoading] = useState(true);
  const [failed, setFailed] = useState(false);
  const [tab, setTabState] = useState<Tab>(initialTab);
  // Until the player picks a tab, a live football match shows the pitch.
  const [tabTouched, setTabTouched] = useState(initialTab !== 'overview');
  const setTab = (x: Tab) => {
    setTabTouched(true);
    setTabState(x);
  };

  useEffect(() => {
    let alive = true;
    let timer: ReturnType<typeof setTimeout> | null = null;
    const load = async () => {
      const d = await getMatchDetails(fi);
      if (!alive) return;
      setLoading(false);
      if (d) {
        setDetails(d);
        setFailed(false);
      } else {
        setFailed(true);
      }
      if (d?.state === 'live') {
        timer = setTimeout(load, LIVE_REFRESH_MS);
      }
    };
    setLoading(true);
    void load();
    return () => {
      alive = false;
      if (timer) clearTimeout(timer);
    };
  }, [fi]);

  // A match in play may have video, when the admin has it on.
  const live = details?.state === 'live';
  const [stream, setStream] = useState<MatchStream | null>(null);
  useEffect(() => {
    if (!live) {
      setStream(null);
      return;
    }
    let alive = true;
    void getMatchStream(fi).then((s) => alive && setStream(s));
    return () => {
      alive = false;
    };
  }, [fi, live]);

  // Live football: the BetZone tracker in the Live tab when the match has
  // one (LIVE_TRACKER in config/constants); else, or when it fails to load,
  // our own drawn pitch.
  const liveFootball = live && details?.sport_id === 1;
  const [tracker, setTracker] = useState<MatchTracker | null>(null);
  const [trackerFailed, setTrackerFailed] = useState(false);
  useEffect(() => {
    setTracker(null);
    setTrackerFailed(false);
    if (!liveFootball || LIVE_TRACKER !== 'betzone') return;
    let alive = true;
    let timer: ReturnType<typeof setTimeout> | null = null;
    const ask = async () => {
      const x = await getMatchTracker(fi);
      if (!alive) return;
      if (x) setTracker(x);
      else timer = setTimeout(ask, TRACKER_RETRY_MS);
    };
    void ask();
    return () => {
      alive = false;
      if (timer) clearTimeout(timer);
    };
  }, [fi, liveFootball]);

  const tabs = useMemo(() => {
    if (!details) return [] as Tab[];
    const out: Tab[] = [];
    // A football match in play opens on the drawn pitch.
    if (details.state === 'live' && details.sport_id === 1) out.push('pitch');
    if (stream) out.push('video');
    out.push('overview');
    if (details.timeline?.length) out.push('timeline');
    if (
      details.history?.home?.length ||
      details.history?.away?.length ||
      (h2h === 'include' && details.history?.h2h?.length)
    ) {
      out.push('form');
    }
    if (firstTable(details.table)?.overall?.tables?.length) out.push('table');
    if (details.lineup?.home || details.lineup?.away) out.push('lineups');
    return out;
  }, [details, h2h, stream]);

  if (loading && !details) return <Loader />;
  if (failed && !details) {
    return <div className={styles.empty}>{t('match_details_unavailable')}</div>;
  }
  if (!details) return null;

  const current = tabs.includes(tab)
    ? tab === 'overview' && tabs[0] === 'pitch' && !tabTouched
      ? 'pitch'
      : tab
    : 'overview';

  if (h2h === 'only') {
    const rows = details.history?.h2h ?? [];
    return (
      <div className={styles.wrapper}>
        {!hideScoreboard && <Scoreboard details={details} />}
        <div className={styles.body}>
          {rows.length > 0 ? (
            <FormBlock title={t('match_h2h')} rows={rows} />
          ) : (
            <div className={styles.empty}>{t('match_no_h2h')}</div>
          )}
        </div>
      </div>
    );
  }

  return (
    <div className={styles.wrapper}>
      {!hideScoreboard && <Scoreboard details={details} odds={heroOdds} />}
      <div className={styles.tabs} role="tablist">
        {tabs.map((x) => (
          <button
            key={x}
            type="button"
            role="tab"
            aria-selected={x === current}
            className={classNames(styles.tab, {
              [styles.tabActive]: x === current,
            })}
            onClick={() => setTab(x)}
          >
            {t(`match_tab_${x}`)}
          </button>
        ))}
      </div>
      <div className={styles.body}>
        {current === 'video' && stream && (
          <MatchVideo fi={fi} stream={stream} />
        )}
        {current === 'pitch' && tracker && !trackerFailed && (
          <BetZoneTracker
            gameid={tracker.gameid}
            sport={tracker.sport}
            home={details.home.name}
            away={details.away.name}
            onFail={() => setTrackerFailed(true)}
          />
        )}
        {current === 'pitch' && !(tracker && !trackerFailed) && (
          <LivePitch details={details}>
            <ul className={styles.timeline}>
              {(details.timeline ?? [])
                .filter(isKeyEvent)
                .slice(-3)
                .reverse()
                .map((x) => {
                  const kind = eventKind(x.text);
                  return (
                    <li key={x.id} data-kind={kind}>
                      <span className={styles.eventIcon} aria-hidden="true">
                        {EVENT_ICON[kind]}
                      </span>
                      {cleanEventText(x.text)}
                    </li>
                  );
                })}
            </ul>
          </LivePitch>
        )}
        {liveOdds && (current === 'pitch' || current === 'video') && (
          <div className={styles.liveOdds}>{liveOdds}</div>
        )}
        {current === 'overview' && <Overview details={details} t={t} />}
        {current === 'timeline' && (
          <Timeline items={details.timeline ?? []} t={t} />
        )}
        {current === 'form' && (
          <Form details={details} t={t} withH2h={h2h === 'include'} />
        )}
        {current === 'table' && <Table details={details} t={t} />}
        {current === 'lineups' && <Lineups details={details} t={t} />}
      </div>
    </div>
  );
};

const firstTable = (table: MatchDetails['table']): LeagueTable | undefined =>
  Array.isArray(table) ? table[0] : table;

const Crest = ({ team, size = 's' }: { team: TeamRef; size?: 's' | 'm' }) => {
  const [broken, setBroken] = useState(false);
  const src = teamLogoUrl(team.image_id, size);
  if (!src || broken) {
    return <span className={styles.crestBlank} aria-hidden="true" />;
  }
  return (
    <img
      className={size === 'm' ? styles.crestM : styles.crest}
      src={src}
      alt=""
      loading="lazy"
      onError={() => setBroken(true)}
    />
  );
};

const Flag = ({ cc }: { cc?: string | null }) => {
  const [broken, setBroken] = useState(false);
  const src = flagUrl(cc);
  if (!src || broken) return null;
  return (
    <img
      className={styles.flag}
      src={src}
      alt=""
      loading="lazy"
      onError={() => setBroken(true)}
    />
  );
};

const Scoreboard = ({
  details,
  odds,
}: {
  details: MatchDetails;
  odds?: ReactNode;
}) => {
  const { t } = useTranslation();
  const live = useContext(LiveBettingContext);
  const halfTime = (live?.events?.get(1) as LiveEvent[] | undefined)?.find(
    (e) => String(e.fi) === String(details.fi)
  )?.halfTime;
  const score = details.ss?.replace('-', ' : ');
  return (
    <div className={styles.scoreboard}>
      <div className={styles.league}>
        <Flag cc={details.league.cc} />
        {details.league.name}
        {details.extra?.round && (
          <span className={styles.muted}>
            {' '}
            · {t('match_round')} {details.extra.round}
          </span>
        )}
      </div>
      <div className={styles.teamsRow}>
        <div className={styles.team}>
          <Crest team={details.home} size="m" />
          <span>{details.home.name}</span>
        </div>
        <div className={styles.score}>
          {details.state === 'upcoming' ? (
            <span className={styles.kickoff}>
              {details.time > 0
                ? formatDateTime(new Date(details.time * 1000), {
                    removeSeconds: true,
                    removeYear: true,
                  })
                : '-'}
            </span>
          ) : (
            <span>{score || '-'}</span>
          )}
          <span
            className={classNames(styles.state, {
              [styles.stateLive]: details.state === 'live',
            })}
          >
            {halfTime ? t('live_half_time') : t(`match_state_${details.state}`)}
          </span>
        </div>
        <div className={classNames(styles.team, styles.teamAway)}>
          <Crest team={details.away} size="m" />
          <span>{details.away.name}</span>
        </div>
      </div>
      {odds}
    </div>
  );
};

const periodLabels = (
  sportId: number,
  t: TFunction
): Record<string, string> => {
  switch (sportId) {
    case 1:
      return {
        1: t('period_ht'),
        2: t('period_ft'),
        3: t('period_et'),
        4: t('period_pens'),
      };
    case 18:
      return {
        1: 'Q1',
        2: 'Q2',
        3: t('period_ht'),
        4: 'Q3',
        5: 'Q4',
        6: 'OT',
        7: t('period_total'),
      };
    case 17:
      return { 1: 'P1', 2: 'P2', 3: 'P3', 4: 'OT', 5: t('period_total') };
    default:
      return {};
  }
};

const Overview = ({ details, t }: { details: MatchDetails; t: TFunction }) => {
  const labels = periodLabels(details.sport_id, t);
  const periods = Object.entries(details.scores ?? {}).sort(
    ([a], [b]) => Number(a) - Number(b)
  );
  // In play, the last period is the one being played, not a final score.
  const lastPeriod = periods.length ? periods[periods.length - 1][0] : '';
  const stats = details.stats ?? {};
  const statKeys = [
    ...STAT_ORDER.filter((k) => stats[k]),
    ...Object.keys(stats).filter((k) => !STAT_ORDER.includes(k)),
  ];
  const extra = details.extra;
  const info: [string, string | undefined][] = [
    [t('match_referee'), extra?.referee?.name],
    [
      t('match_venue'),
      [extra?.stadium_data?.name, extra?.stadium_data?.city]
        .filter(Boolean)
        .join(', ') || undefined,
    ],
    [t('match_capacity'), extra?.stadium_data?.capacity],
    [
      t('match_managers'),
      extra?.home_manager?.name || extra?.away_manager?.name
        ? `${extra?.home_manager?.name ?? '-'} / ${extra?.away_manager?.name ?? '-'}`
        : undefined,
    ],
    [
      t('match_positions'),
      extra?.home_pos || extra?.away_pos
        ? `${extra?.home_pos ?? '-'}. / ${extra?.away_pos ?? '-'}.`
        : undefined,
    ],
  ];

  return (
    <>
      {periods.length > 0 && (
        <div className={styles.periods}>
          {periods.map(([k, v]) => (
            <div key={k} className={styles.period}>
              <span className={styles.muted}>
                {details.state === 'live' && k === lastPeriod
                  ? t('period_current')
                  : labels[k] ?? k}
              </span>
              <span>
                {v.home ?? '-'}:{v.away ?? '-'}
              </span>
            </div>
          ))}
        </div>
      )}
      {statKeys.length > 0 ? (
        <div className={styles.stats}>
          {statKeys.map((k) => (
            <StatBar key={k} name={k} pair={stats[k]} t={t} />
          ))}
        </div>
      ) : (
        details.state !== 'upcoming' && (
          <div className={styles.empty}>{t('match_no_stats_yet')}</div>
        )
      )}
      {info.some(([, v]) => v) && (
        <div className={styles.info}>
          {info
            .filter(([, v]) => v)
            .map(([label, value]) => (
              <div key={label}>
                <span className={styles.muted}>{label}</span>
                <span>{value}</span>
              </div>
            ))}
        </div>
      )}
    </>
  );
};

const StatBar = ({
  name,
  pair,
  t,
}: {
  name: string;
  pair: [string, string];
  t: TFunction;
}) => {
  const h = Number.parseFloat(pair?.[0] ?? '');
  const a = Number.parseFloat(pair?.[1] ?? '');
  if (!Number.isFinite(h) || !Number.isFinite(a)) return null;
  const ratio = RATIO_STATS.has(name);
  const fmt = (v: number) =>
    ratio ? `${Math.round(v * (v <= 1 ? 100 : 1))}%` : String(v);
  const total = h + a;
  const homeShare = total > 0 ? (h / total) * 100 : 50;
  return (
    <div className={styles.stat}>
      <div className={styles.statLine}>
        <span className={classNames({ [styles.lead]: h > a })}>
          {name === 'possession_rt' ? `${h}%` : fmt(h)}
        </span>
        <span className={styles.statName}>
          {t(`stat_${name}`, { defaultValue: name.replace(/_/g, ' ') })}
        </span>
        <span className={classNames({ [styles.lead]: a > h })}>
          {name === 'possession_rt' ? `${a}%` : fmt(a)}
        </span>
      </div>
      <div
        className={classNames(styles.bar, { [styles.barNone]: total === 0 })}
      >
        <span className={styles.barHome} style={{ width: `${homeShare}%` }} />
        <span
          className={styles.barAway}
          style={{ width: `${100 - homeShare}%` }}
        />
      </div>
    </div>
  );
};

const Timeline = ({
  items,
  t,
}: {
  items: { id: string; text: string }[];
  t: TFunction;
}) => {
  const [all, setAll] = useState(false);
  const shown = (all ? items : items.filter(isKeyEvent)).slice().reverse();
  return (
    <>
      <div className={styles.filterRow}>
        <button
          type="button"
          className={classNames(styles.chip, { [styles.chipOn]: !all })}
          onClick={() => setAll(false)}
        >
          {t('timeline_key_events')}
        </button>
        <button
          type="button"
          className={classNames(styles.chip, { [styles.chipOn]: all })}
          onClick={() => setAll(true)}
        >
          {t('timeline_all_events')} ({items.length})
        </button>
      </div>
      <ul className={styles.timeline}>
        {shown.map((x) => {
          const kind = eventKind(x.text);
          return (
            <li key={x.id} data-kind={kind}>
              <span className={styles.eventIcon} aria-hidden="true">
                {EVENT_ICON[kind]}
              </span>
              {cleanEventText(x.text)}
            </li>
          );
        })}
        {shown.length === 0 && (
          <li className={styles.empty}>{t('match_no_events_yet')}</li>
        )}
      </ul>
    </>
  );
};

/** The feed separates some lines with "~" ("79' ~ Substitution ~ A ~ B ~(England)"). */
const cleanEventText = (text: string) =>
  text
    .replace(/\s*~\s*\(/g, ' (')
    .replace(/\s*~\s*/g, ' - ')
    .replace(/\)\]$/, ')');

const isKeyEvent = (x: { text: string }) =>
  KEY_EVENT.test(x.text) && !NOISE_EVENT.test(x.text);

type EventKind =
  | 'goal'
  | 'yellow'
  | 'red'
  | 'sub'
  | 'corner'
  | 'period'
  | 'other';

const EVENT_ICON: Record<EventKind, string> = {
  goal: '⚽',
  yellow: '🟨',
  red: '🟥',
  sub: '🔁',
  corner: '⛳',
  period: '⏱',
  other: '•',
};

const eventKind = (text: string): EventKind => {
  if (/score after|half score|full time/i.test(text)) return 'period';
  if (/red card/i.test(text)) return 'red';
  if (/yellow card/i.test(text)) return 'yellow';
  if (/goal/i.test(text) && !/goal kick|attempt/i.test(text)) return 'goal';
  if (/substitut/i.test(text)) return 'sub';
  if (/corner/i.test(text)) return 'corner';
  return 'other';
};

const Form = ({
  details,
  t,
  withH2h,
}: {
  details: MatchDetails;
  t: TFunction;
  withH2h: boolean;
}) => {
  const h = details.history ?? {};
  return (
    <>
      <FormBlock
        title={details.home.name}
        team={details.home}
        rows={h.home ?? []}
      />
      <FormBlock
        title={details.away.name}
        team={details.away}
        rows={h.away ?? []}
      />
      {withH2h && h.h2h && h.h2h.length > 0 && (
        <FormBlock title={t('match_h2h')} rows={h.h2h} />
      )}
    </>
  );
};

/** W / D / L for a team from a past match ("ss" home-away). */
const outcomeFor = (m: HistoryMatch, team?: TeamRef) => {
  const parts = (m.ss ?? '').split('-').map((x) => Number.parseInt(x, 10));
  if (!team || parts.length !== 2 || parts.some((x) => !Number.isFinite(x))) {
    return null;
  }
  const isHome =
    (team.events_id && m.home.id === team.events_id) ||
    m.home.name === team.name;
  const [mine, theirs] = isHome ? parts : [parts[1], parts[0]];
  return mine > theirs ? 'W' : mine < theirs ? 'L' : 'D';
};

const FormBlock = ({
  title,
  team,
  rows,
}: {
  title?: string;
  team?: TeamRef;
  rows: HistoryMatch[];
}) => {
  const { t } = useTranslation();
  if (rows.length === 0) return null;
  const outcomes = rows.map((m) => outcomeFor(m, team)).filter(Boolean);
  return (
    <div className={styles.block}>
      <div className={styles.blockTitle}>
        {team && <Crest team={team} />}
        <span>{title}</span>
        {outcomes.length > 0 && (
          <span className={styles.formStrip}>
            {outcomes.slice(0, 5).map((o, i) => (
              <span key={i} data-outcome={o}>
                {t(`form_${o}`)}
              </span>
            ))}
          </span>
        )}
      </div>
      {rows.map((m) => (
        <div key={m.id} className={styles.historyRow}>
          <span className={styles.muted}>
            {formatDateTime(new Date(Number(m.time) * 1000), {
              removeSeconds: true,
              removeTime: true,
            })}
          </span>
          <span className={styles.historyTeams}>
            <span>{m.home.name}</span>
            <span>{m.away.name}</span>
          </span>
          <span className={styles.historyHalf} title={t('half_time_label')}>
            {m.ht ? `(${m.ht.replace('-', ':')})` : ''}
          </span>
          <span
            className={styles.historyScore}
            data-outcome={outcomeFor(m, team)}
          >
            {m.ss ? m.ss.replace('-', ':') : '-'}
          </span>
        </div>
      ))}
    </div>
  );
};

const Table = ({ details, t }: { details: MatchDetails; t: TFunction }) => {
  const table = firstTable(details.table);
  const ids = [details.home.events_id, details.away.events_id];
  return (
    <>
      {table?.season?.name && (
        <div className={styles.muted}>{table.season.name}</div>
      )}
      {(table?.overall?.tables ?? []).map((tb, i) => {
        // A cup lists every group; the match's group first, others folded.
        const ours = tb.rows.some((r) => ids.includes(r.team.id));
        if (!ours && (table?.overall?.tables?.length ?? 0) > 1) return null;
        return (
          <div key={i} className={styles.block}>
            {tb.name && <div className={styles.blockTitle}>{tb.name}</div>}
            <table className={styles.standings}>
              <thead>
                <tr>
                  <th>#</th>
                  <th className={styles.left}>{t('table_team')}</th>
                  <th>{t('table_w')}</th>
                  <th>{t('table_d')}</th>
                  <th>{t('table_l')}</th>
                  <th>{t('table_goals')}</th>
                  <th>{t('table_points')}</th>
                </tr>
              </thead>
              <tbody>
                {tb.rows.map((r) => (
                  <tr
                    key={r.team.id ?? r.pos}
                    className={classNames({
                      [styles.ourRow]: ids.includes(r.team.id),
                    })}
                  >
                    <td>{r.pos}</td>
                    <td className={styles.left}>
                      <span className={styles.tableTeam}>
                        <Crest team={r.team} />
                        {r.team.name}
                      </span>
                    </td>
                    <td>{r.win}</td>
                    <td>{r.draw}</td>
                    <td>{r.loss}</td>
                    <td>
                      {r.goalsfor}:{r.goalsagainst}
                    </td>
                    <td className={styles.points}>{r.points}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        );
      })}
    </>
  );
};

const Lineups = ({ details, t }: { details: MatchDetails; t: TFunction }) => {
  const side = (team: TeamRef, lineup?: TeamLineup) =>
    lineup ? (
      <div className={styles.lineup}>
        <div className={styles.blockTitle}>
          <Crest team={team} />
          <span>{team.name}</span>
          {lineup.formation && (
            <span className={styles.muted}>{lineup.formation}</span>
          )}
        </div>
        {(lineup.startinglineup ?? []).map((p, i) => (
          <div key={i} className={styles.player}>
            <span className={styles.shirt}>{p.shirtnumber ?? ''}</span>
            <span>{p.player.name}</span>
            <span className={styles.muted}>
              {p.pos
                ? t(`lineup_pos_${p.pos.toLowerCase()}`, {
                    defaultValue: p.pos,
                  })
                : ''}
            </span>
          </div>
        ))}
        {(lineup.substitutes ?? []).length > 0 && (
          <>
            <div className={styles.subsTitle}>{t('lineup_substitutes')}</div>
            {(lineup.substitutes ?? []).map((p, i) => (
              <div key={i} className={classNames(styles.player, styles.sub)}>
                <span className={styles.shirt}>{p.shirtnumber ?? ''}</span>
                <span>{p.player.name}</span>
              </div>
            ))}
          </>
        )}
      </div>
    ) : null;
  return (
    <div className={styles.lineups}>
      {side(details.home, details.lineup?.home)}
      {side(details.away, details.lineup?.away)}
    </div>
  );
};
